import {Grid, Box, Stack, Statistic, Card, Text} from '@primer/react-brand/esm'
import type {Document} from '@contentful/rich-text-types'
import type {Asset} from '@github-ui/swp-core/schemas/contentful/contentTypes/asset'
import type {TextItem} from '../../types'
import {renderWithoutParagraphs} from '../../utils'

export type FlexSuiteTestimonialStatProps = {
  quote: Document
  name?: string
  title?: string
  link: {href: string; text: string}
  label?: string
  image: Asset
  stats?: TextItem[]
}

export function FlexSuiteTestimonialStat({
  quote,
  name,
  title,
  link,
  image,
  label,
  stats,
}: FlexSuiteTestimonialStatProps) {
  if (!stats?.length) return null

  return (
    <Box borderBlockEndWidth="thin" borderBlockStartWidth="thin" borderColor="muted" borderStyle="solid">
      <Grid enableGutters={false} columnGap="none" rowGap="none">
        <Grid.Column span={{large: 8}}>
          <Box
            padding={{narrow: 32, regular: 32, wide: 64}}
            backgroundColor="subtle"
            borderInlineStartWidth={{wide: 'thin'}}
            borderInlineEndWidth={{wide: 'thin'}}
            borderColor="muted"
            borderStyle="solid"
            style={{height: '100%'}}
          >
            <Card href={link?.href ?? ''} fullWidth variant="minimal" disableAnimation>
              {label && <Card.Label>{label}</Card.Label>}
              <Card.Heading size="5">{renderWithoutParagraphs(quote)}</Card.Heading>
              {(name || title) && (
                <Card.Description>
                  <Text size="200" weight="semibold">
                    {name}
                  </Text>
                  {title && <Text size="200">, {title}</Text>}
                </Card.Description>
              )}
              <Card.Image
                position="block-end"
                aspectRatio="16:10"
                src={image.fields.file.url}
                alt={image.fields.description ?? ''}
              />
            </Card>
          </Box>
        </Grid.Column>
        <Grid.Column span={{large: 4}}>
          <Box borderInlineEndWidth={{wide: 'thin'}} borderColor="muted" borderStyle="solid" style={{height: '100%'}}>
            <Stack direction="vertical" padding="none" gap="none" style={{height: '100%'}}>
              {stats.map((stat, index) => (
                <Box
                  key={stat.sys.id}
                  padding={{narrow: 32, wide: 64}}
                  borderBlockStartWidth={index === 0 ? {narrow: 'thin', wide: 'none'} : 'thin'}
                  borderColor="muted"
                  borderStyle="solid"
                  style={{flex: 1, display: 'flex', alignItems: 'center'}}
                >
                  <Statistic>
                    <Statistic.Heading weight="normal" size="800" style={{color: 'var(--brand-color-text-emphasized)'}}>
                      {renderWithoutParagraphs(stat.fields.heading)}
                    </Statistic.Heading>
                    <Statistic.Description font="monospace" size="100">
                      {renderWithoutParagraphs(stat.fields.description)}
                    </Statistic.Description>
                  </Statistic>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid.Column>
      </Grid>
    </Box>
  )
}
